/**
 * Section: Unique Sellings Points Carousel
 * ------------------------------------------------------------------------------
 * Large component displaying a carousel with icons and arrow controls
 *
 * @namespace uspsCarousel
 */
import { register } from '@shopify/theme-sections';
import { on } from '~/helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  id: 'UspsCarousel',
  iconIndicator: '[js-usps-carousel="icon-indicator"]',
  prevButton: '[js-usps-carousel="prev"]',
  nextButton: '[js-usps-carousel="next"]',
  headerTextContainer: '[js-usps-carousel="header-text"]',
  contentTextContainer: '[js-usps-carousel="content-text"]',
  initialActiveSlide: '[data-position="1"]'
};
/**
 * Carousel section constructor.
 * Executes on page load as well as Theme Editor `section:load` events.
 */

register('homepage-usps--carousel', {
  /**
   * Cache selectors.
   * - Define necessary variables and make available for component use.
   */
  cacheSelectors() {
    const container = "#".concat(selectors.id, "-").concat(this.id);
    const prevButton = "".concat(container, " ").concat(selectors.prevButton);
    const nextButton = "".concat(container, " ").concat(selectors.nextButton);
    const iconIndicator = "".concat(container, " ").concat(selectors.iconIndicator);
    const headerTextContainer = "".concat(container, " ").concat(selectors.headerTextContainer);
    const contentTextContainer = "".concat(container, " ").concat(selectors.contentTextContainer);
    this.cache = {
      container,
      nextButton,
      prevButton,
      iconIndicator,
      headerTextContainer,
      contentTextContainer
    };
  },

  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.container = document.querySelector(this.cache.container);
    this.prevButton = document.querySelector(this.cache.prevButton);
    this.nextButton = document.querySelector(this.cache.nextButton);
    this.iconIndicators = document.querySelectorAll(this.cache.iconIndicator);
    this.headerTextContainer = document.querySelector(this.cache.headerTextContainer);
    this.contentTextContainer = document.querySelector(this.cache.contentTextContainer);
  },

  /**
   * Set icon click listeners
   */
  setIconCickListeners() {
    this.iconIndicators.forEach(iconIndicator => {
      on('click', iconIndicator, () => this.handleIconClick(event));
    });
  },

  handleIconClick() {
    if (!event.target.matches(selectors.iconIndicator)) {
      return;
    }

    this.activeSlide = event.target;
    this.setIconClasses();
    this.setContent();
  },

  /**
   * Set arrow click listeners
   */
  setArrowCickListeners() {
    on('click', this.nextButton, () => this.nextSlide());
    on('click', this.previousButton, () => this.previousSlide());
  },

  nextSlide() {
    if (!event.target.matches(selectors.nextButton)) {
      return;
    }

    this.currentSlidePosition = this.getCurrentSlidePosition();

    if (this.currentSlidePosition >= this.iconIndicators.length) {
      this.setInitialActiveSlide();
      this.setContent();
    } else {
      this.activeSlide = this.container.querySelector("[data-position=\"".concat(this.currentSlidePosition + 1, "\"]"));
    }

    this.setIconClasses();
    this.setContent();
  },

  previousSlide() {
    if (!event.target.matches(selectors.prevButton)) {
      return;
    }

    this.currentSlidePosition = this.getCurrentSlidePosition();

    if (this.currentSlidePosition === 1) {
      this.activeSlide = this.container.querySelector("[data-position=\"".concat(this.iconIndicators.length, "\"]"));
      this.setContent();
    } else {
      this.activeSlide = this.container.querySelector("[data-position=\"".concat(this.currentSlidePosition - 1, "\"]"));
    }

    this.setIconClasses();
    this.setContent();
  },

  getCurrentSlidePosition() {
    return parseInt(this.activeSlide.dataset.position, 10);
  },

  setContent() {
    this.headerTextContainer.classList.add('faded-out');
    this.contentTextContainer.classList.add('faded-out');
    setTimeout(() => {
      this.headerTextContainer.innerText = this.activeSlide.dataset.headerText;
      this.contentTextContainer.innerText = this.activeSlide.dataset.contentText;
      this.headerTextContainer.classList.remove('faded-out');
      this.contentTextContainer.classList.remove('faded-out');
    }, 400);
  },

  setIconClasses() {
    this.iconIndicators.forEach(icon => {
      icon.classList.remove('active');
    });
    this.activeSlide.classList.add('active');
  },

  setInitialActiveSlide() {
    this.activeSlide = this.container.querySelector(selectors.initialActiveSlide);
  },

  /**
   * Event callback when Section is registered in scripts.
   */
  onLoad() {
    this.cacheSelectors();
    this.setNodeSelectors();
    this.setIconCickListeners();
    this.setArrowCickListeners();
    this.setInitialActiveSlide();
  }

});